/**
 * Note Save Queue - the single persistence path for note content/title edits.
 *
 * Design (see NOTES_LIFECYCLE_PLAN_2026-06-11.md, invariant I4):
 * - Callers only ever enqueue patches; the queue drains itself after a short
 *   debounce. There is no parallel "direct save" path.
 * - Patches for the same note merge ({title} + {contentMarkdown} coexist), so
 *   a title save can never evict queued content.
 * - A completed save only removes the pending entry if it is still the exact
 *   snapshot that was sent - newer keystrokes queued mid-flight survive and
 *   trigger an immediate follow-up drain.
 * - Retries with exponential backoff; keepalive fetch on page unload.
 * - Save lifecycle events let the UI show "Saving..." and let the stores
 *   upsert saved notes without a second save mechanism.
 */

import { updateNote as apiUpdateNote } from '@/api/notesApi';
import type { Note } from '@/types/note';

export interface NoteSavePatch {
  contentMarkdown?: string;
  title?: string | null;
}

interface PendingSave {
  noteId: string;
  updates: NoteSavePatch;
  retryCount: number;
}

export type NoteSavePhase = 'queued' | 'saving' | 'saved' | 'failed';

export interface NoteSaveEvent {
  noteId: string;
  phase: NoteSavePhase;
  note?: Note;
  error?: Error;
}

const DRAIN_DELAY_MS = 1500;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

// One pending (merged) patch per note.
const pendingSaves = new Map<string, PendingSave>();
// Notes with a PATCH currently in flight.
const savingNotes = new Set<string>();
// Per-note drain timers.
const drainTimers = new Map<string, ReturnType<typeof setTimeout>>();
// One-shot waiters resolved when the current in-flight save settles.
const settleWaiters = new Map<string, Array<(note: Note | null, error?: Error) => void>>();
// Persistent listeners for save lifecycle events.
const saveListeners = new Set<(event: NoteSaveEvent) => void>();

function emit(event: NoteSaveEvent): void {
  for (const listener of saveListeners) {
    try {
      listener(event);
    } catch (error) {
      console.error('Note save listener failed:', error);
    }
  }
}

export function subscribeNoteSaves(listener: (event: NoteSaveEvent) => void): () => void {
  saveListeners.add(listener);
  return () => saveListeners.delete(listener);
}

function settle(noteId: string, note: Note | null, error?: Error): void {
  const waiters = settleWaiters.get(noteId) || [];
  settleWaiters.delete(noteId);
  for (const waiter of waiters) {
    waiter(note, error);
  }
}

function scheduleDrain(noteId: string, delay = DRAIN_DELAY_MS): void {
  const existing = drainTimers.get(noteId);
  if (existing) {
    clearTimeout(existing);
  }
  drainTimers.set(
    noteId,
    setTimeout(() => {
      drainTimers.delete(noteId);
      void processSave(noteId);
    }, delay)
  );
}

/**
 * Queue a note save. Merges with any pending patch for the note and
 * (re)schedules the self-drain.
 */
export function queueNoteSave(noteId: string, updates: NoteSavePatch): void {
  const existing = pendingSaves.get(noteId);
  pendingSaves.set(noteId, {
    noteId,
    updates: { ...existing?.updates, ...updates },
    retryCount: 0,
  });
  emit({ noteId, phase: 'queued' });
  scheduleDrain(noteId);
}

async function processSave(noteId: string): Promise<Note | null> {
  if (savingNotes.has(noteId)) {
    // The in-flight completion handler re-drains if anything is still pending.
    return null;
  }
  const snapshot = pendingSaves.get(noteId);
  if (!snapshot) {
    return null;
  }

  savingNotes.add(noteId);
  emit({ noteId, phase: 'saving' });

  try {
    const note = await apiUpdateNote(noteId, snapshot.updates);
    savingNotes.delete(noteId);

    // Only clear the entry if no newer patch replaced our snapshot mid-flight.
    if (pendingSaves.get(noteId) === snapshot) {
      pendingSaves.delete(noteId);
    } else {
      scheduleDrain(noteId, 0);
    }

    emit({ noteId, phase: 'saved', note });
    settle(noteId, note);
    return note;
  } catch (error) {
    savingNotes.delete(noteId);
    const err = error instanceof Error ? error : new Error(String(error));

    const current = pendingSaves.get(noteId);
    if (current === snapshot && snapshot.retryCount < MAX_RETRIES) {
      snapshot.retryCount += 1;
      scheduleDrain(noteId, RETRY_DELAY_MS * Math.pow(2, snapshot.retryCount - 1));
      return null;
    }
    if (current && current !== snapshot) {
      // Newer content arrived while we were failing; let it try fresh.
      scheduleDrain(noteId, 0);
      return null;
    }

    console.error(`Failed to save note ${noteId} after ${MAX_RETRIES} retries:`, err);
    pendingSaves.delete(noteId);
    emit({ noteId, phase: 'failed', error: err });
    settle(noteId, null, err);
    return null;
  }
}

/**
 * Merge updates into the queue and flush them immediately, resolving with the
 * saved note. The caller's updates are never discarded: if a save is already
 * in flight, they stay queued and are sent right after it settles.
 */
export async function saveNoteNow(noteId: string, updates: NoteSavePatch): Promise<Note> {
  queueNoteSave(noteId, updates);
  const note = await flushNoteSave(noteId);
  if (!note) {
    throw new Error(`Failed to save note ${noteId}`);
  }
  return note;
}

/**
 * Flush any pending save for a note and wait until the queue is empty for it.
 * Resolves with the last saved note (or null if nothing was pending).
 */
export async function flushNoteSave(noteId: string): Promise<Note | null> {
  const timer = drainTimers.get(noteId);
  if (timer) {
    clearTimeout(timer);
    drainTimers.delete(noteId);
  }

  if (savingNotes.has(noteId)) {
    // Wait for the in-flight save, then flush whatever is still queued.
    const settled = await new Promise<Note | null>((resolve) => {
      const waiters = settleWaiters.get(noteId) || [];
      waiters.push((note) => resolve(note));
      settleWaiters.set(noteId, waiters);
    });
    if (pendingSaves.has(noteId)) {
      return flushNoteSave(noteId);
    }
    return settled;
  }

  if (!pendingSaves.has(noteId)) {
    return null;
  }

  const note = await processSave(noteId);
  // A mid-flight replacement leaves a fresh pending entry; flush it too.
  if (pendingSaves.has(noteId) && !savingNotes.has(noteId)) {
    return flushNoteSave(noteId);
  }
  return note;
}

export function hasPendingSave(noteId: string): boolean {
  return pendingSaves.has(noteId) || savingNotes.has(noteId);
}

export function hasAnyPendingSaves(): boolean {
  return pendingSaves.size > 0 || savingNotes.size > 0;
}

/**
 * Flush all pending saves and wait for completion.
 */
export async function flushAllSaves(): Promise<void> {
  const noteIds = new Set([...pendingSaves.keys(), ...savingNotes.keys()]);
  await Promise.all([...noteIds].map((noteId) => flushNoteSave(noteId).catch(() => null)));
}

/**
 * Discard any queued (unsent) patch for a note. Used when the note itself is
 * being deleted/discarded so the queue doesn't resurrect it.
 */
export function discardPendingSave(noteId: string): void {
  const timer = drainTimers.get(noteId);
  if (timer) {
    clearTimeout(timer);
    drainTimers.delete(noteId);
  }
  pendingSaves.delete(noteId);
}

/**
 * Use fetch with keepalive so unsent patches survive page unload.
 * Best-effort: no store updates, no feedback.
 */
function keepaliveSave(noteId: string, updates: NoteSavePatch): void {
  const token = localStorage.getItem('authToken');
  if (!token) return;

  fetch(`/api/notes/${noteId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-access-token': token,
    },
    body: JSON.stringify(updates),
    keepalive: true,
  }).catch(() => {
    // Best-effort save on unload.
  });
}

function handleBeforeUnload(): void {
  for (const save of pendingSaves.values()) {
    keepaliveSave(save.noteId, save.updates);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', handleBeforeUnload);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushAllSaves();
    }
  });
}
